/* eslint-disable @typescript-eslint/camelcase */
import { AxiosResponse } from "axios";
import { PickupCorporateAddress } from "@/domain/entities/PickupSchedule";
import {
  PickupCorporateScheduleConfig,
  PickupScheduleRegularDay,
  PickupScheduleSpecialDate,
  PickupScheduleTimeSlot
} from "@/domain/entities/PickupCorporateScheduleConfig";
import {
  PickupCorporateScheduleConfigCreateRequest,
  PickupCorporateScheduleConfigUpdateRequest
} from "@/data/payload/api/PickupCorporateScheduleConfigApiRequest";
import {
  PickupCorporateScheduleConfigApiAddress,
  PickupCorporateScheduleConfigApiData,
  PickupCorporateScheduleConfigApiDay,
  PickupCorporateScheduleConfigApiResponse,
  PickupCorporateScheduleConfigApiSpecialDate,
  PickupCorporateScheduleConfigApiTimeSlot
} from "@/data/payload/api/PickupCorporateScheduleConfigApiResponse";

export class PickupCorporateScheduleConfigMapper {
  public mapResponseToEntity(
    result: AxiosResponse<PickupCorporateScheduleConfigApiResponse>
  ): PickupCorporateScheduleConfig {
    const responseData = result.data?.data;
    const raw = (Array.isArray(responseData)
      ? {}
      : responseData ?? {}) as PickupCorporateScheduleConfigApiData;
    const clientName = String(raw.client_name ?? "");
    if (!raw || !raw.ps_id) {
      return new PickupCorporateScheduleConfig({ clientName });
    }

    const regularDays = this.mapRegularDays(raw.days);
    const specialDates = this.mapSpecialDates(raw.special_dates);
    return new PickupCorporateScheduleConfig({
      scheduleId: Number(raw.ps_id) || 0,
      clientName: String(raw.client_name ?? ""),
      estimateAdditionalKoli: String(
        raw.ps_estimate_total_koli ?? raw.ps_estimate_additional_koli ?? ""
      ),
      estimateWeight: String(raw.ps_estimate_total_tonase ?? ""),
      serviceCodes: this.mapServiceCodes(raw.ps_service_codes),
      regularEnabled: regularDays.length > 0,
      regularDays,
      specialEnabled: specialDates.length > 0,
      specialDates,
      address: this.mapAddress(raw.pickup_corporate_address)
    });
  }

  public mapEntityToCreateRequest(
    config: PickupCorporateScheduleConfig
  ): PickupCorporateScheduleConfigCreateRequest {
    return new PickupCorporateScheduleConfigCreateRequest(config);
  }

  public mapEntityToUpdateRequest(
    config: PickupCorporateScheduleConfig
  ): PickupCorporateScheduleConfigUpdateRequest {
    return new PickupCorporateScheduleConfigUpdateRequest(config);
  }

  private mapRegularDays(
    days?: PickupCorporateScheduleConfigApiDay[]
  ): PickupScheduleRegularDay[] {
    return (days || []).map(
      (day) =>
        new PickupScheduleRegularDay({
          day: String(day.day ?? ""),
          times: this.mapTimes(day.times)
        })
    );
  }

  private mapSpecialDates(
    dates?: PickupCorporateScheduleConfigApiSpecialDate[]
  ): PickupScheduleSpecialDate[] {
    return (dates || []).map(
      (date) =>
        new PickupScheduleSpecialDate({
          date: String(date.date ?? ""),
          times: this.mapTimes(date.times)
        })
    );
  }

  private mapTimes(
    times?: PickupCorporateScheduleConfigApiTimeSlot[]
  ): PickupScheduleTimeSlot[] {
    return (times || []).map(
      (time) =>
        new PickupScheduleTimeSlot({
          start: String(time.start ?? ""),
          end: String(time.end ?? "")
        })
    );
  }

  private mapServiceCodes(value: unknown): string[] {
    return String(value ?? "")
      .split(",")
      .map((code) => code.trim())
      .filter(Boolean);
  }

  private mapAddress(
    address?: PickupCorporateScheduleConfigApiAddress
  ): PickupCorporateAddress {
    return new PickupCorporateAddress({
      pcaId: Number(address?.pca_id ?? 0),
      pcaClientId: Number(address?.pca_client_id ?? 0),
      pcaIsDefault: Boolean(address?.pca_is_default),
      pcaName: String(address?.pca_name ?? ""),
      pcaLatlon: String(address?.pca_latlon ?? ""),
      pcaPhoneNumber: String(address?.pca_phone_number ?? ""),
      pcaAddress: String(address?.pca_address ?? ""),
      pcaPicName: String(address?.pca_pic_name ?? ""),
      pcaPostalCode: String(address?.pca_postal_code ?? ""),
      pcaDistrictId: Number(address?.pca_district_id ?? 0)
    });
  }
}
